import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'

/**
 * The surface everything sits on.
 *
 * `flat` is the one without a shadow, for a card inside another card — two
 * elevations stacked reads as a mistake rather than as depth.
 */
const card = cva('bg-surface rounded-lg border border-hairline', {
  variants: {
    elevation: { flat: '', raised: 'shadow-e1' },
    padding: { none: 'p-0', sm: 'p-5', md: 'p-6', lg: 'p-8' },
  },
  defaultVariants: { elevation: 'raised', padding: 'md' },
})

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof card> {
  /**
   * Render as the child rather than as a `<div>`.
   *
   * For a card that is itself pressable — a figure that opens the rows it
   * counts. A `<div>` with an `onClick` is not a button to a keyboard or to
   * a screen reader, and nesting a full-size button inside a card puts a
   * focus ring on the wrong rectangle.
   */
  asChild?: boolean
}

export function Card({ className, elevation, padding, asChild, ...props }: CardProps) {
  const Component = asChild ? Slot : 'div'
  return <Component className={cn(card({ elevation, padding }), className)} {...props} />
}

/** Title, optional description, and whatever sits on the right of the same line. */
export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <Text as="h3" variant="card-title">
          {title}
        </Text>
        {description && (
          <Text variant="secondary" className="mt-1">
            {description}
          </Text>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mt-4', className)} {...props} />
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mt-6 flex items-center gap-2', className)} {...props} />
}
